import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ExternalLink, Globe2, MapPin, Tent } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Counter } from '@/components/initiative/Counter';

interface HostCountry {
  id: string;
  name: { en: string; ar: string };
  flagEmoji: string;
  registered: number;
  /** Camps total = official + unofficial */
  campsOfficial: number;
  campsUnofficial: number;
  emphasis: { en: string; ar: string };
  highlight?: boolean;
}

// All figures sourced from UNRWA (Where We Work pages, factsheets 2024–2025)
// and Wikipedia's "Palestinian refugee camps". Last verified early 2026.
const HOSTS: HostCountry[] = [
  {
    id: 'jordan',
    name: { en: 'Jordan', ar: 'الأردن' },
    flagEmoji: '🇯🇴',
    registered: 2_390_000,
    campsOfficial: 10,
    campsUnofficial: 3,
    emphasis: {
      en: 'Largest Palestinian refugee population in the region',
      ar: 'أكبر تجمّع للاجئين الفلسطينيين في المنطقة',
    },
    highlight: true,
  },
  {
    id: 'westbank',
    name: { en: 'West Bank · East Jerusalem', ar: 'الضفة الغربية · القدس الشرقية' },
    flagEmoji: '🇵🇸',
    registered: 934_000,
    campsOfficial: 19,
    campsUnofficial: 4,
    emphasis: {
      en: 'Densest camp network in occupied territory',
      ar: 'أكبر شبكة مخيمات داخل الأراضي المحتلة',
    },
  },
  {
    id: 'syria',
    name: { en: 'Syria', ar: 'سوريا' },
    flagEmoji: '🇸🇾',
    registered: 438_000,
    campsOfficial: 9,
    campsUnofficial: 3,
    emphasis: {
      en: 'Down from 560k pre-war · ~40% internally displaced',
      ar: 'انخفض من 560 ألفاً قبل الحرب · 40٪ نازحون داخلياً',
    },
  },
  {
    id: 'lebanon',
    name: { en: 'Lebanon', ar: 'لبنان' },
    flagEmoji: '🇱🇧',
    registered: 500_000,
    campsOfficial: 12,
    campsUnofficial: 0,
    emphasis: {
      en: 'Severe restrictions on work and property',
      ar: 'قيود قاسية على العمل والتملك',
    },
  },
];

const SOURCES = [
  {
    label: 'UNRWA · Where we work (Jordan, Lebanon, Syria, West Bank)',
    href: 'https://www.unrwa.org/where-we-work',
  },
  {
    label: 'UNRWA · Lebanon factsheet 2025',
    href: 'https://www.unrwa.org/sites/default/files/content/resources/lebanon_factsheet_2025.pdf',
  },
  {
    label: 'UNRWA · Jordan factsheet 2025',
    href: 'https://www.unrwa.org/sites/default/files/content/resources/jordan_factsheet_2025.pdf',
  },
  {
    label: 'UNRWA · Syria, Lebanon, Jordan emergency appeal 2025',
    href: 'https://www.unrwa.org/resources/emergency-appeals/syria-lebanon-and-jordan-emergency-appeal-2025',
  },
];

function formatRefugees(n: number, lang: 'en' | 'ar'): string {
  return new Intl.NumberFormat('en-US').format(n);
}

export function PalestinianDiasporaAnalytics() {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const lang = isRTL ? 'ar' : 'en';

  const totalCamps = HOSTS.reduce((s, h) => s + h.campsOfficial + h.campsUnofficial, 0);
  const totalRegistered = HOSTS.reduce((s, h) => s + h.registered, 0);

  return (
    <section className="relative py-24 lg:py-32 bg-surface-1 overflow-hidden scroll-mt-20">
      <img
        aria-hidden
        src="/img/initiative/analytics-diaspora.webp"
        alt=""
        className="absolute inset-0 h-full w-full object-cover opacity-45 pointer-events-none"
        loading="lazy"
      />
      <div aria-hidden className="absolute inset-0 bg-gradient-to-b from-surface-1/85 via-surface-1/70 to-surface-1/90 pointer-events-none" />
      <div className="absolute inset-0 bg-line-grid opacity-20" />
      <motion.div
        aria-hidden
        animate={{ scale: [1, 1.1, 1], opacity: [0.15, 0.3, 0.15] }}
        transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute -bottom-32 -left-32 w-[36rem] h-[36rem] bg-accent/10 rounded-full blur-3xl"
      />

      <div className="container mx-auto px-4 relative">
        {/* Header */}
        <div className="grid lg:grid-cols-12 gap-6 mb-14 items-end">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-8"
          >
            <div className="section-mark text-accent">
              ※ {isRTL ? 'خارج غزة' : 'Beyond Gaza'}
            </div>
            <h2
              className={cn(
                'mt-4 font-display font-black uppercase leading-[0.95] text-balance',
                'text-5xl lg:text-6xl xl:text-7xl max-w-4xl',
                isRTL && 'font-display-ar normal-case leading-[1.1]'
              )}
            >
              {isRTL ? (
                <>
                  المخيمات في الدول{' '}
                  <span className="bg-gradient-to-r from-accent to-accent/60 bg-clip-text text-transparent">
                    العربية المضيفة.
                  </span>
                </>
              ) : (
                <>
                  Camps across the{' '}
                  <span className="bg-gradient-to-r from-accent to-accent/60 bg-clip-text text-transparent">
                    Arab host countries.
                  </span>
                </>
              )}
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="lg:col-span-4 font-editorial-italic text-base lg:text-lg text-foreground/70 leading-relaxed"
          >
            "{isRTL
              ? 'غزة جزء من شبكة أوسع. ملايين الفلسطينيين يعيشون في مخيمات الأردن وسوريا ولبنان والضفة — وكلها تحتاج مدرّبين أيضاً.'
              : 'Gaza is one node in a wider network. Millions live in camps across Jordan, Syria, Lebanon and the West Bank — every one of them needs coaches too.'}"
          </motion.p>
        </div>

        {/* Country grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {HOSTS.map((host, idx) => {
            const totalHostCamps = host.campsOfficial + host.campsUnofficial;
            return (
              <motion.div
                key={host.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.55,
                  delay: Math.min(idx * 0.08, 0.32),
                  ease: [0.2, 0.8, 0.2, 1],
                }}
                whileHover={{ y: -6 }}
                className={cn(
                  'group relative p-6 lg:p-7 rounded-2xl border-2 backdrop-blur-xl overflow-hidden transition-colors duration-500',
                  host.highlight
                    ? 'bg-surface-2 border-accent/60 shadow-2xl shadow-accent/5'
                    : 'bg-surface-2/60 border-border/60 hover:border-accent/40'
                )}
              >
                <div
                  aria-hidden
                  className={cn(
                    'absolute -top-12 -right-12 h-40 w-40 rounded-full blur-3xl pointer-events-none transition-opacity duration-500',
                    host.highlight
                      ? 'bg-accent/15 opacity-100'
                      : 'bg-accent/0 group-hover:bg-accent/10 opacity-0 group-hover:opacity-100'
                  )}
                />

                <div className="relative flex items-center justify-between mb-4">
                  <span
                    className="text-3xl leading-none"
                    role="img"
                    aria-label={host.name.en}
                  >
                    {host.flagEmoji}
                  </span>
                  <span className="font-mono-mark text-[10px] uppercase tracking-widest text-muted-foreground">
                    / 0{idx + 1}
                  </span>
                </div>

                <div className="relative font-display font-black uppercase text-base lg:text-lg leading-tight text-foreground">
                  {host.name[lang]}
                </div>

                {/* Registered refugees */}
                <div className="relative mt-5">
                  <div className="font-mono-mark text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">
                    {isRTL ? 'لاجئون مسجَّلون' : 'Registered refugees'}
                  </div>
                  <div
                    className={cn(
                      'font-display font-black num-tabular leading-none',
                      'text-3xl lg:text-4xl',
                      host.highlight ? 'text-accent' : 'text-foreground'
                    )}
                  >
                    <Counter
                      value={host.registered}
                      format={(v) => formatRefugees(Math.round(v), lang)}
                    />
                  </div>
                </div>

                {/* Camps */}
                <div className="relative mt-5 pt-5 border-t border-border/40 grid grid-cols-2 gap-3">
                  <div>
                    <div className="font-mono-mark text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                      {isRTL ? 'مخيمات رسمية' : 'Official'}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Tent className="h-3.5 w-3.5 text-accent" />
                      <span className="font-display font-black text-xl num-tabular text-foreground">
                        <Counter value={host.campsOfficial} />
                      </span>
                    </div>
                  </div>
                  <div>
                    <div className="font-mono-mark text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                      {isRTL ? 'غير رسمية' : 'Unofficial'}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5 text-foreground/50" />
                      <span className="font-display font-black text-xl num-tabular text-foreground/70">
                        <Counter value={host.campsUnofficial} />
                      </span>
                    </div>
                  </div>
                  <div className="col-span-2 mt-1 pt-2 border-t border-border/30">
                    <div className="font-mono-mark text-[9px] uppercase tracking-widest text-accent/80">
                      {isRTL ? 'إجمالي المخيمات' : 'Total camps'}: {totalHostCamps}
                    </div>
                  </div>
                </div>

                <p className="relative mt-4 text-xs text-foreground/65 leading-relaxed font-editorial-italic">
                  "{host.emphasis[lang]}"
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Aggregate totals strip */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55, delay: 0.2 }}
          className="mt-8 p-6 lg:p-7 rounded-2xl border-2 border-accent/40 bg-surface-2 relative overflow-hidden"
        >
          <div className="grid sm:grid-cols-3 gap-6 items-center">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-accent/15 border border-accent/40 flex items-center justify-center flex-shrink-0">
                <Globe2 className="h-5 w-5 text-accent" />
              </div>
              <div className="font-mono-mark text-[10px] uppercase tracking-widest text-foreground/65 leading-relaxed">
                {isRTL
                  ? 'الخلاصة الإقليمية — خارج غزة'
                  : 'Regional totals — beyond Gaza'}
              </div>
            </div>
            <div>
              <div className="font-mono-mark text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                {isRTL ? 'لاجئون مسجَّلون' : 'Registered refugees'}
              </div>
              <div className="font-display font-black text-3xl lg:text-4xl num-tabular text-accent leading-none">
                <Counter
                  value={totalRegistered}
                  format={(v) => formatRefugees(Math.round(v), lang)}
                />
              </div>
            </div>
            <div>
              <div className="font-mono-mark text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                {isRTL ? 'مخيم في الدول الأربع' : 'Camps across 4 host countries'}
              </div>
              <div className="font-display font-black text-3xl lg:text-4xl num-tabular text-foreground leading-none">
                <Counter value={totalCamps} />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Sources */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-8 pt-6 border-t border-border/40"
        >
          <div className="font-mono-mark text-[10px] uppercase tracking-widest text-muted-foreground mb-3">
            {isRTL ? 'المصادر' : 'Sources'}
          </div>
          <ul className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-foreground/70">
            {SOURCES.map((src) => (
              <li key={src.href + src.label}>
                <a
                  href={src.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 hover:text-accent transition-colors"
                >
                  <span>{src.label}</span>
                  <ExternalLink className="h-3 w-3" />
                </a>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-[11px] font-editorial-italic text-foreground/55 leading-relaxed max-w-3xl">
            {isRTL
              ? 'الأرقام مُجمَّعة من تقارير الأونروا الرسمية وموسوعة المخيمات الفلسطينية — قد تتغيّر مع المستجدات.'
              : 'Figures compiled from UNRWA "Where we work" pages, country factsheets, and the Palestinian refugee-camps reference. Numbers shift with each reporting cycle.'}
          </p>
        </motion.div>
      </div>
    </section>
  );
}
